import React, { useState, useEffect, useRef, useMemo, useCallback, } from 'react';
import shortid from 'shortid';
import { throttle, noop } from 'lodash-es';
import styled, { css } from '../../styled';
import { TooltipView } from '../../index';
import TableHead from './TableHead';
import TableBody from './TableBody';
const classNamePrefix = shortid.generate();
const fixedClasses = {
    head: `${classNamePrefix}-fixed-head`,
    column: `${classNamePrefix}-fixed-column`,
    columnLeft: `${classNamePrefix}-fixed-column-left`,
    columnRight: `${classNamePrefix}-fixed-column-right`,
};
const FixedTooltipContainer = styled.div.attrs(({ top, left }) => ({ style: { top, left } })) `
  position: fixed;
  transform: translateY(-50%);
  pointer-events: none;
  z-index: 1;
`;
const TableWrapper = styled.div `
  ${({ maxHeight }) => css `
    max-height: ${`${maxHeight}rem`};
    overflow: auto;
  `}
`;
const StyledTable = styled.table `
  ${({ theme, backgroundColor, fixedTop, fixedLeft, fixedRight }) => css `
    width: 100%;
    border-collapse: collapse;
    border-spacing: 0;
    ${fixedTop &&
    `.${fixedClasses.head}::after {
        border: 0.03125rem solid ${theme.colors.INK_300};
      }`}
    ${fixedLeft &&
    `.${fixedClasses.columnLeft}::before {
        box-shadow: inset 6px 0 6px -3px rgba(0, 0, 0, 0.15);
      }`}
    ${fixedRight &&
    `.${fixedClasses.columnRight}::before {
        box-shadow: inset -6px 0 6px -3px rgba(0, 0, 0, 0.15);
      }`}

    .${fixedClasses.head} {
      position: sticky;
      top: 0;
      z-index: ${theme.zIndex.TableFixedHead};
      background-color: ${backgroundColor || theme.colors.SNOW_200};
      &::after {
        pointer-events: none;
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: -0.0625rem;
      }
    }

    .${fixedClasses.column} {
      position: sticky;
      z-index: ${theme.zIndex.TableFixedColumn};
      background-color: ${backgroundColor || theme.colors.SNOW_200};
      &.${fixedClasses.head} {
        z-index: ${theme.zIndex.TableFixedHeadAndColumn};
        transition: 0;
      }
      &.${fixedClasses.columnLeft}::before {
        content: '';
        position: absolute;
        width: 0.5rem;
        top: 0;
        bottom: 0;
        right: 0;
        transform: translateX(100%);
        transition: 0.3s;
      }
      &.${fixedClasses.columnRight}::before {
        content: '';
        position: absolute;
        width: 0.5rem;
        top: 0;
        bottom: 0;
        left: 0;
        transform: translateX(-100%);
        transition: 0.3s;
      }
    }
  `}
`;
const getTooltipPosition = (tableContainer, rowElement) => {
    if (!tableContainer)
        return { left: 0, top: 0 };
    const tableRect = tableContainer.getBoundingClientRect();
    const left = tableRect.left + tableRect.width / 2;
    const rowRect = rowElement.getBoundingClientRect();
    const top = rowRect.top + rowRect.height / 2;
    return { left, top };
};
const Table = ({ columns, dataSource, maxHeight, fixedHead, headColumnAlign = 'left', rowVerticalAlign = 'middle', backgroundColor, hoverable = true, indexWidth = 3.125, indexTitle, renderIndex, onClickSortColumn, onTableRowClick, lang, rowHeight, withHead = true, expandRows, collapseIcon, collapseRowStyle, e2eId, }) => {
    const ref = useRef(null);
    const [fixedPosition, setFixedPosition] = useState({});
    const [fixedTooltip, setFixedTooltip] = useState(null);
    const columnConfig = useMemo(() => {
        let leftOffsetCount = indexTitle || renderIndex ? indexWidth : 0;
        let rightOffsetCount = columns
            .filter(column => column.fixed === 'right')
            .reduce((count, column) => {
            const newCount = count + (column.width || 0);
            return newCount;
        }, 0);
        return columns.reduce((config, column) => {
            // fixedColumns
            if (column.fixed) {
                Object.assign(config.fixedColumns, {
                    [column.key]: column.fixed,
                });
            }
            // fixedColumnAlign
            Object.assign(config.fixedColumnAlign, {
                [column.key]: column.align || 'left',
            });
            // fixedOffsetLeft / fixedLeftShadowColumnKey
            if (column.fixed === 'left') {
                Object.assign(config.fixedOffsetLeft, {
                    [column.key]: leftOffsetCount,
                });
                Object.assign(config, { fixedLeftShadowColumnKey: column.key });
                leftOffsetCount += column.width || 0;
            }
            // fixedOffsetRight / fixedRightShadowColumnKey
            if (column.fixed === 'right') {
                if (!config.fixedRightShadowColumnKey) {
                    Object.assign(config, { fixedRightShadowColumnKey: column.key });
                }
                rightOffsetCount -= column.width || 0;
                Object.assign(config.fixedOffsetRight, {
                    [column.key]: rightOffsetCount,
                });
            }
            return config;
        }, {
            fixedColumns: {},
            fixedColumnAlign: {},
            fixedOffsetLeft: {},
            fixedOffsetRight: {},
            fixedLeftShadowColumnKey: '',
            fixedRightShadowColumnKey: '',
        });
    }, [indexWidth, indexTitle, renderIndex, columns]);
    const tableStyleHandler = useCallback(throttle(() => {
        if (ref.current) {
            const { scrollTop, scrollLeft, scrollWidth, clientWidth } = ref.current;
            setFixedPosition({
                fixedTop: scrollTop > 0,
                fixedLeft: scrollLeft > 0,
                fixedRight: scrollLeft + clientWidth !== scrollWidth,
            });
        }
    }, 200), []);
    const onTrMouseEnter = useCallback((e, data) => {
        if (data.tooltip && ref.current) {
            setFixedTooltip(Object.assign(Object.assign(Object.assign({}, data.tooltip), getTooltipPosition(ref.current, e.currentTarget)), { rowElement: e.currentTarget }));
        }
        else {
            setFixedTooltip(null);
        }
    }, []);
    const onTrMouseLeave = useCallback(() => {
        setFixedTooltip(null);
    }, []);
    const withTooltip = !!fixedTooltip;
    useEffect(() => {
        if (!withTooltip)
            return noop;
        const handleScroll = throttle(() => {
            setFixedTooltip(prevState => prevState && Object.assign(Object.assign({}, prevState), getTooltipPosition(ref.current, prevState.rowElement)));
        }, 100);
        document.addEventListener('scroll', handleScroll, true);
        return () => {
            document.removeEventListener('scroll', handleScroll, true);
        };
    }, [withTooltip]);
    useEffect(() => {
        const tableElement = ref.current;
        if (tableElement &&
            (Object.keys(columnConfig.fixedColumns).length || fixedHead)) {
            window.addEventListener('resize', tableStyleHandler);
            tableElement.addEventListener('scroll', tableStyleHandler);
            tableStyleHandler();
        }
        return () => {
            if (tableElement) {
                window.removeEventListener('resize', tableStyleHandler);
                tableElement.removeEventListener('scroll', tableStyleHandler);
            }
        };
    }, [columnConfig.fixedColumns, fixedHead, tableStyleHandler]);
    return (React.createElement(TableWrapper, { ref: ref, maxHeight: maxHeight },
        React.createElement(StyledTable, Object.assign({ backgroundColor: backgroundColor }, fixedPosition),
            withHead && (React.createElement(TableHead, { columns: columns, columnConfig: columnConfig, hoverable: hoverable, indexWidth: indexWidth, indexTitle: indexTitle, renderIndex: renderIndex, fixedClasses: fixedClasses, fixedHead: fixedHead, headColumnAlign: headColumnAlign, onClickSortColumn: onClickSortColumn, isCollapse: !!collapseIcon, lang: lang })),
            React.createElement(TableBody, { columns: columns, dataSource: dataSource, columnConfig: columnConfig, hoverable: hoverable, renderIndex: renderIndex, fixedClasses: fixedClasses, rowVerticalAlign: rowVerticalAlign, onTableRowClick: onTableRowClick, rowHeight: rowHeight, expandRows: expandRows, collapseIcon: collapseIcon, collapseRowStyle: collapseRowStyle, onTrMouseEnter: onTrMouseEnter, onTrMouseLeave: onTrMouseLeave, e2eId: e2eId })),
        fixedTooltip && (React.createElement(FixedTooltipContainer, Object.assign({}, fixedTooltip),
            React.createElement(TooltipView, { show: true, direction: "TOP_CENTER", text: fixedTooltip.text, type: fixedTooltip.type || 'primary' })))));
};
export default React.memo(Table);
//# sourceMappingURL=index.js.map